import React, { useState, useCallback, useEffect } from 'react';
import { useField } from 'formik';
import { useDropzone } from 'react-dropzone';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import { MEDIA, usePatientsActions, useAlerts, FILE_ID } from 'store/patients';
import Spinner from 'components/FullScreenSpinner';
import Portal, { IMAGES_REVIEW } from 'components/Portal';
import defaultProfilePhoto from 'assets/img/profilePhoto.png';

import Icon from './Icon';
import Close from './Close';
import { Container, Image, UploadButton, Message, CloseButton, ReviewContainer, ReviewWrapper, ReviewImage } from './styles';

const Portrait = ({ readMode }) => {
  const { id } = useParams();
  const [isOpenReview, setIsOpenReview] = useState();
  const handleOpenReview = useCallback(() => setIsOpenReview(($) => !$), []);
  const [{ value: media = {} }, , { setValue: setMedia }] = useField(MEDIA);
  const { uploadMedia, loadFile } = usePatientsActions();
  const { action, loading, success, error, resetAlerts } = useAlerts(uploadMedia);
  const { id: actionId, action: loadMedia, loading: mediaLoading, success: portrait } = useAlerts(loadFile);
  const isLoading = loading || (media?.[FILE_ID] && (!actionId || mediaLoading));
  const onDrop = useCallback(
    ([file]) => {
      if (file) action({ id, file });
    },
    [action, id]
  );
  const {
    getRootProps,
    getInputProps,
    isDragActive: btnIsDragActive,
    isDragReject: btnIsDragReject,
  } = useDropzone({
    onDrop,
    accept: ['image/png', 'image/jpeg'],
  });
  const {
    getRootProps: imgGetRootProps,
    getInputProps: imgGetInputProps,
    isDragActive: imgIsDragActive,
    isDragReject: imgIsDragReject,
  } = useDropzone({
    onDrop,
    accept: ['image/png', 'image/jpeg'],
  });
  const isDragActive = btnIsDragActive || imgIsDragActive;
  const isDragReject = btnIsDragReject || imgIsDragReject;

  useEffect(() => {
    if (success) {
      setMedia(success);
      resetAlerts();
    }
  }, [resetAlerts, setMedia, success]);

  useEffect(() => {
    if (media?.[FILE_ID]) loadMedia(media);
  }, [loadMedia, media]);

  return (
    <>
      <Container error={error}>
        {!(readMode || media?.[FILE_ID]) && (
          <Image
            type="button"
            src={defaultProfilePhoto}
            {...imgGetRootProps()}
            borderColor={(isDragReject && '#f79253') || (isDragActive && '#83dfb4')}
            disabled={isLoading}
          >
            <input {...imgGetInputProps()} />
            {isLoading && <Spinner height={120} />}
            <Icon />
          </Image>
        )}
        {Boolean(portrait) && (
          <Image type="button" src={portrait} onClick={handleOpenReview} disabled={loading}>
            {isLoading && <Spinner height={120} />}
          </Image>
        )}
        {!readMode && (
          <UploadButton
            type="button"
            color={(isDragReject && 'warning') || (isDragActive && 'success') || 'normal'}
            {...getRootProps()}
            disabled={isLoading}
          >
            <input {...getInputProps()} />
            {isDragReject && 'Gültige Dateiformate .JPEG und .PNG'}
            {!isDragReject && media?.[FILE_ID] && 'Foto ändern'}
            {!isDragReject && !media?.[FILE_ID] && 'Foto hochladen'}
          </UploadButton>
        )}
        {!readMode && error && (
          <Message type="error" small={1}>
            {error}
          </Message>
        )}
      </Container>
      {Boolean(portrait && isOpenReview) && (
        <Portal type={IMAGES_REVIEW}>
          <ReviewContainer>
            <ReviewWrapper>
              <CloseButton type="button" onClick={handleOpenReview}>
                <Close />
              </CloseButton>
              <ReviewImage src={portrait} />
            </ReviewWrapper>
          </ReviewContainer>
        </Portal>
      )}
    </>
  );
};

Portrait.propTypes = { readMode: PropTypes.bool.isRequired };

export default Portrait;
